import { Component, HostListener, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { MenuModule } from 'primeng/menu';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { APP_CONSTANTS } from '../../core/constants/app.constants';
import { Constants } from '../../core/constants/core.constants';
import { SidebarService } from '../../core/Provider/sidebar.service';
import { AuthService } from '../../core/Provider/auth.service';
import { InboxService } from '../../services/inbox.service';
import { ROLE_PERMISSIONS } from '../../core/permissions/role-permissions';
import { normalizeRoleNameForPermissions } from '../../core/utils/role-normalization.utils';
import { getStoredUserRole } from '../../core/utils/user-storage.utils';

interface MenuItem {
  label: string;
  icon: string;
  iconPath: string;
  route: string;
  badgeCount?: number;
  children?: SubMenuItem[];
}

interface SubMenuItem {
  label: string;
  route: string;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, MenuModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit, OnDestroy {
  private sidebarService = inject(SidebarService);
  private authService = inject(AuthService);
  private inboxService = inject(InboxService);
  private router = inject(Router);
  private subscription = new Subscription();

  readonly inboxRoute = APP_CONSTANTS.ROUTES.INBOX;
  inboxUnreadCount = 0;

  logo = Constants.logo;
  logoIcon = Constants.logoIcon;
  sidebarCollapseIcon = Constants.sidebarCollapse;
  sidebarExpandIcon = Constants.sidebarExpand;
  chevronRightIcon = Constants.chevronRight;
  isCollapsed = false;
  isMobile = false;
  expandedItems: Set<string> = new Set();
  private readonly BREAKPOINT = 1200;
  currentRoute: string = '';
  menuItems: MenuItem[] = []; // Public property for template binding

  ngOnInit(): void {
    this.checkWindowSize();
    // On desktop, ensure sidebar is visible by default
    if (!this.isMobile) {
      this.isCollapsed = false;
      this.sidebarService.setCollapsed(false);
    }

    // Subscribe to current user to update menu based on role dynamically
    this.subscription.add(
      this.authService.currentUser$.subscribe(user => {
        this.initializeMenu(user?.roleName);
        if (!user) {
          this.inboxUnreadCount = 0;
        }
      })
    );

    // Initial check (in case behavior subject already has value or we rely on localStorage as fallback)
    this.initializeMenu();

    // Track current route
    this.currentRoute = this.router.url;
    this.subscription.add(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe((event: any) => {
        this.currentRoute = event.url;
        // Auto-expand the parent menu if a child route is active
        this.autoExpandActiveMenu();
        if (event.url.startsWith(this.inboxRoute)) {
          this.loadInboxUnreadCount();
        }
      })
    );

    // Auto-expand on initial load
    this.autoExpandActiveMenu();

    this.loadInboxUnreadCount();
    this.subscription.add(
      this.inboxService.countsUpdated$.subscribe(() => this.loadInboxUnreadCount())
    );

    // Subscribe to sidebar service for external toggles (from header)
    this.subscription.add(
      this.sidebarService.isCollapsed$.subscribe(collapsed => {
        this.isCollapsed = collapsed;
        // Close all submenus when sidebar collapses
        if (collapsed) {
          this.expandedItems.clear();
        }
      })
    );
  }
 private initializeMenu(roleName?: string): void {
  // Step 1: Get role from argument or localStorage
  let role: string = roleName || getStoredUserRole();
  role = normalizeRoleNameForPermissions(role);

  // Step 2: Filter menu items based on role
  this.menuItems = this.baseMenuItems.filter(item => {
    // DASHBOARD → Admin, Manager & TeamLead
    if (item.route === APP_CONSTANTS.ROUTES.DASHBOARD) {
      return ROLE_PERMISSIONS.DASHBOARD.includes(role);
    }

    // USERS → only Admin & Manager
    if (item.route === APP_CONSTANTS.ROUTES.USERS) {
      return ROLE_PERMISSIONS.USERS.includes(role);
    }

    // SETTINGS → KPI / Announcement / Booking Limit only
    if (item.route === APP_CONSTANTS.ROUTES.SETTINGS) {
      if (!ROLE_PERMISSIONS.SETTINGS.includes(role)) {
        return false;
      }
      item.children = [
        { label: 'Update CSA/CSI KPI', route: APP_CONSTANTS.ROUTES.SETTINGS },
        ...(ROLE_PERMISSIONS.ANNOUNCEMENTS.includes(role)
          ? [{ label: 'Announcement', route: APP_CONSTANTS.ROUTES.SETTINGS_ANNOUNCEMENT }]
          : []),
        ...(ROLE_PERMISSIONS.BOOKING_LIMIT.includes(role)
          ? [{ label: 'Student Booking Limit', route: APP_CONSTANTS.ROUTES.SETTINGS_BOOKING_LIMIT }]
          : []),
        ...(ROLE_PERMISSIONS.ACTION_ITEM_FORMS.includes(role)
          ? [{ label: 'Custom Action', route: APP_CONSTANTS.ROUTES.SETTINGS_FORMS }]
          : [])
      ];
      return item.children.length > 0;
    }

    // STUDENT DASHBOARD
    if (item.route === APP_CONSTANTS.ROUTES.STUDENT_DASHBOARD) {
      return ROLE_PERMISSIONS.STUDENT_DASHBOARD.includes(role);
    }

    // INBOX → all roles
    if (item.route === APP_CONSTANTS.ROUTES.INBOX) {
      return ROLE_PERMISSIONS.INBOX.includes(role);
    }

    // CALENDAR
    if (item.route === APP_CONSTANTS.ROUTES.CALENDAR) {
      return ROLE_PERMISSIONS.CALENDAR.includes(role);
    }

    // COMPANY — merge child only for roles that can merge (incl. CSS)
    if (item.route === APP_CONSTANTS.ROUTES.COMPANY) {
      if (!ROLE_PERMISSIONS.COMPANY.includes(role)) {
        return false;
      }
      item.children = item.children?.filter(
        (child) =>
          child.route !== APP_CONSTANTS.ROUTES.COMPANY_MERGE ||
          ROLE_PERMISSIONS.COMPANY_MERGE.includes(role)
      );
      return true;
    }

    // JOB
    if (item.route === APP_CONSTANTS.ROUTES.JOB) {
      return ROLE_PERMISSIONS.JOB.includes(role);
    }
    //REPORT
    if(item.route === APP_CONSTANTS.ROUTES.REPORTS){
      return ROLE_PERMISSIONS.REPORT.includes(role);
    }

    // QUICK APPLICATION (top-level)
    if (item.route === APP_CONSTANTS.ROUTES.QUICK_APPLICATION) {
      return ROLE_PERMISSIONS.APPLICATION.includes(role);
    }

    // APPLICATION
    if (item.route === APP_CONSTANTS.ROUTES.APPLICATIONS) {
      if (!ROLE_PERMISSIONS.APPLICATION.includes(role)) {
        return false;
      }
      // Easy Application → CC / TM excluded via ROLE_PERMISSIONS.EASY_APPLICATION
      if (item.children) {
        const newItem = { ...item };
        newItem.children = item.children.filter(child => {
          if (child.route === '/applications/easy-application') {
            return ROLE_PERMISSIONS.EASY_APPLICATION.includes(role);
          }
          return true;
        });
        item.children = newItem.children;
      }
      return true;
    }

    // INTERVIEWS
    if (item.route === APP_CONSTANTS.ROUTES.INTERVIEWS) {
      if (!ROLE_PERMISSIONS.INTERVIEWS.includes(role)) return false;
      item.children = item.children?.filter(child =>
        child.route !== '/interviews/add' || ROLE_PERMISSIONS.INTERVIEW_ADD.includes(role)
      );
      return true;
    }



if (item.route === APP_CONSTANTS.ROUTES.STUDENTS && item.children) {
  // Check if user has permission to see Students menu
  if (!ROLE_PERMISSIONS.STUDENTS.includes(role)) {
    return false; // Hide Students menu if no permission
  }

  // Filter children based on role permissions
  const restrictedRoutes = ['/students/add'];
  
  // Only Admin, TeamLead, CSE, Manager can access restricted routes (CSI excluded)
  const allowedRolesForRestricted = [
    APP_CONSTANTS.ROLES.CSE_TL,
    APP_CONSTANTS.ROLES.CareerServiceSupervisor,
    APP_CONSTANTS.ROLES.ADMIN,
    APP_CONSTANTS.ROLES.CSE,
    APP_CONSTANTS.ROLES.CareerCoach,
    APP_CONSTANTS.ROLES.TechnicalMentor,
    APP_CONSTANTS.ROLES.MANAGER
  ];

  // Roles allowed for Manage Resumes: Admin, Manager, TeamLead, CSE, and CSI
  const allowedRolesForManageResumes = [
    APP_CONSTANTS.ROLES.CSE_TL,
    APP_CONSTANTS.ROLES.CareerServiceSupervisor,
    APP_CONSTANTS.ROLES.ADMIN,
    APP_CONSTANTS.ROLES.CSE,
    APP_CONSTANTS.ROLES.CareerCoach,
    APP_CONSTANTS.ROLES.TechnicalMentor,
    APP_CONSTANTS.ROLES.MANAGER,
    APP_CONSTANTS.ROLES.CSI
  ];

  item.children = item.children.filter(child => {
    // Filter out "Add New Student" for CSI (but allow Manage Resumes)
    if (restrictedRoutes.includes(child.route)) {
      return allowedRolesForRestricted.includes(role);
    }

    // "Manage Resumes" - allow CSI and other roles
    if (child.route === APP_CONSTANTS.ROUTES.MANAGE_RESUMES) {
      return allowedRolesForManageResumes.includes(role);
    }

    // "My Student" visible for everyone who has STUDENTS permission
    if (child.route === APP_CONSTANTS.ROUTES.STUDENTS) {
      return true;
    }

    return true; // keep other children if any
  });

  return true; // Keep STUDENTS menu visible
}



    return true; // keep other menus visible
  });
}



  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.checkWindowSize();
  }

  private checkWindowSize(): void {
    const windowWidth = window.innerWidth;
    const wasMobile = this.isMobile;
    this.isMobile = windowWidth < this.BREAKPOINT;

    // Only auto-collapse when entering mobile mode, not when already in mobile
    if (this.isMobile && !wasMobile) {
      this.isCollapsed = true;
      this.sidebarService.setCollapsed(true);
    }
    // Auto-expand when going back to desktop
    if (!this.isMobile && wasMobile) {
      this.isCollapsed = false;
      this.sidebarService.setCollapsed(false);
    }
  }

  toggleSidebar(): void {
    this.sidebarService.toggle();
  }

  handleNavClick(item: MenuItem, event: Event): void {
    if (this.isBrowserTabGesture(event) || !item.children?.length) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.toggleSubmenu(item, event);
    if (this.isMobile) {
      this.toggleSidebar();
    }
  }

  onSidebarLinkClick(event: Event): void {
    if (this.isBrowserTabGesture(event)) {
      return;
    }
    event.stopPropagation();
    if (this.isMobile) {
      this.toggleSidebar();
    }
  }

  sidebarHref(route: string): string {
    return this.router.serializeUrl(this.router.createUrlTree([route]));
  }

  /** Ctrl/Cmd/Shift/Alt or middle-click: leave the link for the browser to open in a new tab. */
  private isBrowserTabGesture(event: Event): boolean {
    if (!(event instanceof MouseEvent)) {
      return false;
    }
    return event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey;
  }

  toggleSubmenu(item: MenuItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (item.children && item.children.length > 0) {
      if (this.expandedItems.has(item.route)) {
        this.expandedItems.delete(item.route);
      } else {
        this.expandedItems.add(item.route);
      }
    }
  }

  isExpanded(item: MenuItem): boolean {
    return this.expandedItems.has(item.route);
  }

  hasChildren(item: MenuItem): boolean {
    return !!(item.children && item.children.length > 0);
  }

  isRouteActive(item: MenuItem): boolean {
    if (this.isInboxCustomActionContext()) {
      if (item.route === APP_CONSTANTS.ROUTES.INBOX) return true;
      if (item.route === APP_CONSTANTS.ROUTES.SETTINGS) return false;
    }

    // Check if any child route is active, or if we're under this section (e.g. /company/edit/123)
    if (item.children) {
      const childMatch = item.children.some(child => {
        const exactRoutes = ['/students', '/interviews', '/job', '/company', '/applications', '/users', '/settings'];
        if (exactRoutes.includes(child.route)) {
          return this.currentRoute === child.route || this.currentRoute.startsWith(child.route + '?');
        }
        return this.currentRoute === child.route || this.currentRoute.startsWith(child.route + '?');
      });
      if (childMatch) return true;
      // Also active when we're under this section (edit, view, etc.) so sidebar stays expanded
      return this.currentRoute.startsWith(item.route + '/');
    }
    return this.currentRoute === item.route || this.currentRoute.startsWith(item.route + '/') || this.currentRoute.startsWith(item.route + '?');
  }

  private isInboxCustomActionContext(): boolean {
    const url = this.router.parseUrl(this.router.url);
    const path = '/' + url.root.children['primary']?.segments.map((segment) => segment.path).join('/');
    if (!path.startsWith(APP_CONSTANTS.ROUTES.SETTINGS_FORMS)) return false;
    return url.queryParams['returnUrl'] === APP_CONSTANTS.ROUTES.INBOX;
  }

  autoExpandActiveMenu(): void {
    // Find the menu item that has an active child and expand it
    // Close all other menus first
    this.expandedItems.clear();
    if (this.isInboxCustomActionContext()) return;

    // Find and expand the menu with active child
    for (const item of this.menuItems) {
      if (item.children && this.isRouteActive(item)) {
        this.expandedItems.add(item.route);
        break; // Only expand one menu at a time
      }
    }
  }

  getRouterLinkActiveOptions(route: string): { exact: boolean } {
    try {
      // For exact routes like /students, /interviews, /job, /company, /applications, /users, use exact match
      // This prevents /students/add from making /students active
      const exactRoutes = ['/students', '/interviews', '/job', '/company', '/applications', '/users', '/settings'];
      if (exactRoutes.includes(route)) {
        return { exact: true };
      }
      // For child routes like /students/add, /interviews/add, use exact match
      // This ensures only the current route is active
      return { exact: true };
    } catch (error) {
      // Fallback to exact match if there's any error
      return { exact: true };
    }
  }

  get isOverlayMode(): boolean {
    return this.isMobile && !this.isCollapsed;
  }

  isInboxItem(item: MenuItem): boolean {
    return item.route === this.inboxRoute;
  }

  private hasInboxMenuItem(): boolean {
    return this.menuItems.some((item) => this.isInboxItem(item));
  }

  private loadInboxUnreadCount(): void {
    if (!this.authService.getToken() || !this.hasInboxMenuItem()) {
      this.inboxUnreadCount = 0;
      return;
    }

    this.subscription.add(
      this.inboxService.getInboxHeaderCounts().subscribe({
        next: (counts) => {
          this.inboxUnreadCount = counts.action;
        }
      })
    );
  }

  private readonly baseMenuItems: MenuItem[] = [
    {
      label: 'Dashboard',
      icon: 'pi pi-th-large',
      iconPath: Constants.dashboard,
      route: APP_CONSTANTS.ROUTES.DASHBOARD
    },
    {
      label: 'Calender',
      icon: 'pi pi-calendar',
      iconPath: Constants.calendarMenu,
      route: APP_CONSTANTS.ROUTES.CALENDAR
    },
    {
      label: 'Quick Application',
      icon: 'pi pi-bolt',
      iconPath: Constants.applicationsNew,
      route: APP_CONSTANTS.ROUTES.QUICK_APPLICATION
    },
    {
      label: 'Inbox',
      icon: 'pi pi-inbox',
      iconPath: Constants.inbox,
      route: APP_CONSTANTS.ROUTES.INBOX
    },
    {
      label: 'Student',
      icon: 'pi pi-graduation-cap',
      iconPath: Constants.studentsNew,
      route: APP_CONSTANTS.ROUTES.STUDENTS,
      children: [
        { label: 'My Student', route: APP_CONSTANTS.ROUTES.STUDENTS },
        { label: 'Add New Student', route: '/students/add' },
        { label: 'Manage Resumes', route: APP_CONSTANTS.ROUTES.MANAGE_RESUMES },
      ]
    },
    {
      label: 'Company',
      icon: 'pi pi-building',
      iconPath: Constants.company,
      route: APP_CONSTANTS.ROUTES.COMPANY,
      children: [
        { label: 'All Companies', route: APP_CONSTANTS.ROUTES.COMPANY },
        { label: 'Add New Company', route: '/company/add' },
        { label: 'Company Merge Management', route: APP_CONSTANTS.ROUTES.COMPANY_MERGE }
      ]
    },
    {
      label: 'Jobs',
      icon: 'pi pi-briefcase',
      iconPath: Constants.job,
      route: APP_CONSTANTS.ROUTES.JOB,
      children: [
        { label: 'All Jobs', route: APP_CONSTANTS.ROUTES.JOB },
        { label: 'Add New Job', route: '/job/add' },
        { label: 'Add New Scrap Job', route: APP_CONSTANTS.ROUTES.ADD_SCRAP_JOB }
      ]
    },
    {
      label: 'Application',
      icon: 'pi pi-file-edit',
      iconPath: Constants.applicationsNew,
      route: APP_CONSTANTS.ROUTES.APPLICATIONS,
      children: [
        { label: 'All Applications', route: APP_CONSTANTS.ROUTES.APPLICATIONS },
        { label: 'Easy Application', route: '/applications/easy-application' }
      ]
    },
    {
      label: 'Interview',
      icon: 'pi pi-comments',
      iconPath: Constants.interviewsNew,
      route: APP_CONSTANTS.ROUTES.INTERVIEWS,
      children: [
        { label: 'All Interviews', route: APP_CONSTANTS.ROUTES.INTERVIEWS },
        { label: 'Add New Interview', route: '/interviews/add' },
      ]
    },
    {
      label: 'Users',
      icon: 'pi pi-users',
      iconPath: Constants.usersNew,
      route: APP_CONSTANTS.ROUTES.USERS,
      children: [
        { label: 'All Users', route: APP_CONSTANTS.ROUTES.USERS },
        { label: 'Add New User', route: '/users/add' }
      ]
    },
    {
      label: 'Reports',
      icon: 'pi pi-file-pdf',
      iconPath: Constants.reports,
      route: APP_CONSTANTS.ROUTES.REPORTS
    },
    {
      label: 'Settings',
      icon: 'pi pi-setting',
      iconPath: Constants.setting,
      route: APP_CONSTANTS.ROUTES.SETTINGS,
      children: [
        { label: 'Update CSA/CSI KPI', route: APP_CONSTANTS.ROUTES.SETTINGS },
        { label: 'Announcement', route: APP_CONSTANTS.ROUTES.SETTINGS_ANNOUNCEMENT },
        { label: 'Student Booking Limit', route: APP_CONSTANTS.ROUTES.SETTINGS_BOOKING_LIMIT },
        { label: 'Custom Action', route: APP_CONSTANTS.ROUTES.SETTINGS_FORMS }
      ]
    },
    // {
    //   label: 'Status',
    //   icon: 'pi pi-info-circle',
    //   iconPath: Constants.status,
    //   route: APP_CONSTANTS.ROUTES.STATUS
    // },
    // {
    //   label: 'Audit Logs',
    //   icon: 'pi pi-search',
    //   iconPath: Constants.auditLogs,
    //   route: APP_CONSTANTS.ROUTES.AUDIT_LOGS
    // }
  ];
}

